/**
 * SEO Head Component
 *
 * Sets meta tags and injects JSON-LD schemas
 *
 * Usage:
 * ```tsx
 * <SEOHead
 *   title="Product Name | Silux Mobile"
 *   description="Product description"
 *   productSchema={productData}
 * />
 * ```
 */

import { useEffect } from 'react';
import {
  setMetaTags,
  injectSchema,
  generateProductSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
  SEOMetaData,
  ProductSchema,
  BreadcrumbItem
} from '../utils/seo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
  productSchema?: ProductSchema;
  breadcrumbs?: BreadcrumbItem[];
  faqs?: Array<{ question: string; answer: string }>;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  productSchema,
  breadcrumbs,
  faqs
}) => {
  useEffect(() => {
    // Set meta tags
    const meta: SEOMetaData = {};

    if (title) meta.title = title;
    if (description) meta.description = description;
    if (keywords) meta.keywords = keywords;
    if (ogImage) meta.ogImage = ogImage;
    if (canonical) meta.canonical = canonical;

    // Set current page URL for Open Graph
    meta.ogUrl = window.location.href;

    setMetaTags(meta);

    // Inject schemas
    if (productSchema) {
      const schema = generateProductSchema(productSchema);
      injectSchema(schema, 'product-schema');
    }

    if (breadcrumbs && breadcrumbs.length > 0) {
      const schema = generateBreadcrumbSchema(breadcrumbs);
      injectSchema(schema, 'breadcrumb-schema');
    }

    if (faqs && faqs.length > 0) {
      const schema = generateFAQSchema(faqs);
      injectSchema(schema, 'faq-schema');
    }
  }, [title, description, keywords, ogImage, canonical, productSchema, breadcrumbs, faqs]);

  return null; // This component doesn't render anything
};

export default SEOHead;
